import { formatPlainTime } from '@airreading/common';
import {
  BackgroundCheckStatusLabelMap,
  ClassEligibilityLabelMap,
  CoachOverallGradeLabelMap,
  CoachStatusLabelMap,
  CoachSubStatusLabelMap,
  DayOfWeekLabelMap,
  type CoachPoolFilters,
  type TimeRange,
} from '@airreading/common/data';
import { Temporal } from 'temporal-polyfill';

const formatTime = (plainTime: string) => formatPlainTime(Temporal.PlainTime.from(plainTime), { hour12: true });

const formatTimeRange = ({ day, start, end }: TimeRange) =>
  `${DayOfWeekLabelMap[day].abbr} ${formatTime(start)} - ${formatTime(end)}`;

const booleanLabel = (value: boolean) => (value ? 'Yes' : 'No');

export function coachPoolFilterLabels(filters: CoachPoolFilters): string[] {
  return [
    ...(filters.query ? [`Search: ${filters.query}`] : []),
    ...(filters.statuses ?? []).map((status) => `Status: ${CoachStatusLabelMap[status]}`),
    ...(filters.subStatuses ?? []).map((subStatus) => `Sub-status: ${CoachSubStatusLabelMap[subStatus]}`),
    ...(filters.overallGrades ?? []).flatMap((grade) =>
      grade ? [`Overall grade: ${CoachOverallGradeLabelMap[grade]}`] : [],
    ),
    ...(filters.timeRanges ?? []).map(formatTimeRange),
    ...(filters.timeRanges?.length && typeof filters.untakenTeachersOnly === 'boolean'
      ? [`Untaken teachers only: ${booleanLabel(filters.untakenTeachersOnly)}`]
      : []),
    ...(filters.timeRanges?.length && filters.coachAvailabilityType
      ? [`Availability: ${filters.coachAvailabilityType === 'substitute' ? 'Substitute teaching' : 'Regular schedule'}`]
      : []),
    ...(filters.classEligibilities ?? []).map((eligibility) => ClassEligibilityLabelMap[eligibility]),
    ...(filters.backgroundCheckStatuses ?? []).map((status) => {
      const label = BackgroundCheckStatusLabelMap[status];
      return filters.backgroundCheckStatusesLogicalOperator
        ? `${label} (${filters.backgroundCheckStatusesLogicalOperator})`
        : label;
    }),
    ...(typeof filters.googleMeetTechCheck === 'boolean'
      ? [`Google Meet: ${booleanLabel(filters.googleMeetTechCheck)}`]
      : []),
    ...(typeof filters.hasAvailability === 'boolean'
      ? [`Has availability: ${booleanLabel(filters.hasAvailability)}`]
      : []),
    ...(filters.states ?? []).map((state) => `State: ${state}`),
  ];
}
